import React, { useState, useEffect, useRef } from 'react';
import {
    Paper,
    Button,
    TextField,
    FormControlLabel,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    Alert,
    Typography,
    Box,
    FormGroup,
    Divider,
    CircularProgress,
} from '@mui/material';
import axiosInstance from '../utills/axiosInstance';
import { usePermissions } from '../utills/usePermissions';

const RoleManagement = () => {
    const [roles, setRoles] = useState([]);
    const [form, setForm] = useState({ _id: '', name: '', scopes: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const { canCreate: canCreateRole, canView: canViewRole, canEdit: canEditRole, canDelete: canDeleteRole } = usePermissions();

    const hasFetchedRef = useRef(false);

    const scopeCategories = [
        {
            category: 'Dashboard',
            scopes: [
                'dashboard:view',
            ],
        },
        {
            category: 'Organization',
            scopes: [
                'department:view',
                'department:create',
                'department:edit',
                'designation:view',
                'designation:create',
                'designation:edit',
                'employee:view',
                'employee:create',
                'employee:update',
                'employee:delete',
                'candidate:create',
            ],
        },
        {
            category: 'Attendance and Payroll - Attendance',
            scopes: [
                'attendance:create',
                'od:view',
                'od:review',
                'od:approve',
                'od:reject',
                'attendance:viewAll',
                'leaveBalance:view',
                'leave:apply',
                'leave:view',
                'leave:review',
                'leave:approve',
                'leave:reject',
            ],
        },
        {
            category: 'Attendance and Payroll - Payroll',
            scopes: [
                'payroll:view',
                'payrollComponents:view',
                'payrollComponents:create',
                'payrollComponents:update',
                'payrollComponents:activate',
                'payrollPackage:configure',
                'payRun:create',
                'payRun:process',
                'paySlip:download',
            ],
        },
        {
            category: 'Policy',
            scopes: [
                'leavePolicy:view',
                'leavePolicy:create',
                'leavePolicy:update',
            ],
        },
        {
            category: 'Performance',
            scopes: [
                'performance:view'
            ]
        },
        {
            category: 'Role Managment',
            scopes: [
                'role:view',
                'role:create',
                'role:edit',
                'role:delete'
            ],
        },
        {
            category: 'Templates',
            scopes: [
                'template:create',
                'template:preview',
            ],
        },
        {
            category: 'Offboarding/Exit',
            scopes: [
                'offboarding:create',
                'offboarding:view',
                'offboarding:approveRM',
                'offboarding:approveAdmin',
            ],
        },
    ];

    useEffect(() => {
        if (canViewRole('role') && !hasFetchedRef.current) {
            fetchRoles();
            hasFetchedRef.current = true;
        }
    }, [canViewRole]);

    const fetchRoles = async () => {
        try {
            const res = await axiosInstance.get('/api/roles');
            setRoles(res.data || []);
        } catch (err) {
            setError('Failed to load roles');
        }
    };

    const handleScopeChange = (scope) => {
        setForm(prev => ({
            ...prev,
            scopes: prev.scopes.includes(scope)
                ? prev.scopes.filter(s => s !== scope)
                : [...prev.scopes, scope]
        }));
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setError('Role name is required');
            return;
        }
        setLoading(true);
        try {
            await axiosInstance.post('/api/createrole', form);
            setSuccess('Role created successfully');
            setForm({ name: '', scopes: [] });
            fetchRoles();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to create role');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (role) => {
        setForm({ _id: role._id, name: role.role, scopes: role.scopes });
    };

    const handleUpdate = async () => {
        if (!form.name.trim()) {
            setError('Role name is required');
            return;
        }
        setLoading(true);
        try {
            await axiosInstance.post(`/api/updaterole`, form);
            setSuccess('Role updated successfully');
            setForm({ name: '', scopes: [] });
            fetchRoles();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to update role');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this role?')) return;
        try {
            await axiosInstance.post(`/api/deleterole`, id);
            setSuccess('Role deleted');
            fetchRoles();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to delete role');
        }
    };

    return (
        hasFetchedRef || canViewRole('role') ? (
            <Paper sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>Role Management</Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {
                    (canCreateRole('role') || (canEditRole('role') && form.name)) && (
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                label="Role Name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <Typography variant="h6" gutterBottom>Assign Scopes</Typography>
                            <FormGroup>
                                {scopeCategories.map((category) => (
                                    <Box key={category.category} sx={{ mb: 2 }}>
                                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                            {category.category}
                                        </Typography>
                                        {category.scopes.map((scope) => (
                                            <FormControlLabel
                                                key={scope}
                                                control={
                                                    <Checkbox
                                                        checked={form.scopes.includes(scope)}
                                                        onChange={() => handleScopeChange(scope)}
                                                    />
                                                }
                                                label={scope}
                                            />
                                        ))}
                                        <Divider sx={{ my: 1 }} />
                                    </Box>
                                ))}
                            </FormGroup>
                            <Button
                                variant="contained"
                                onClick={form._id ? handleUpdate : handleSubmit}
                                disabled={loading || !form.name.trim()}
                                sx={{ mt: 2 }}
                            >
                                {loading ? <CircularProgress size={20} /> : (form._id ? 'Update Role' : 'Create Role')}
                            </Button>
                        </Box>
                    )
                }

                <Typography variant="h6" gutterBottom>Existing Roles</Typography>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Role Name</TableCell>
                            <TableCell>Scopes</TableCell>
                            {
                                (canEditRole('role') || canDeleteRole('role')) && (
                                    <TableCell>Actions</TableCell>
                                )
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roles?.map((role) => (
                            <TableRow key={role._id}>
                                <TableCell>{role.role}</TableCell>
                                <TableCell>
                                    {role.scopes.slice(0, 5).join(', ')}
                                    {role.scopes.length > 5 && ` +${role.scopes.length - 5} more`}
                                </TableCell>
                                <TableCell>
                                    {canEditRole('role') && (
                                        <Button size="small" onClick={() => handleEdit(role)}>Edit</Button>
                                    )}
                                    {canDeleteRole('role') && (
                                        <Button size="small" color="error" onClick={() => handleDelete(role._id)}>Delete</Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        ) : (
            <Alert severity="warning">Insufficient permissions to view roles.</Alert>
        )
    );
};

export default RoleManagement;
